import type RAPIER_NS from '@dimforge/rapier3d-compat';
import type { Assists, CarSpec, ControlInput, SurfaceId, SuspensionSpec, WheelSpec } from './types';
import { SURFACES, type SurfaceQuery } from './surfaces';
import { tyreForces, tyreOnSurface, type TyreForce, type TyreSurfaceCoeffs } from './tyre';
import { Drivetrain } from './drivetrain';
import { type V3, type Q4, v3, q4, quatFromAxisAngle, quatMul, add, sub, cross, dot, rotate, normalize, scale, addScaled, clamp, smoothstep, DEG, set, copy, len } from './math';
import { applyAssists } from './assists';

type RapierModule = typeof RAPIER_NS;

export const COLLISION_GROUP_CAR = 0x0002;
export const COLLISION_GROUP_WORLD = 0x0001;
/** Interaction groups: membership << 16 | filter. */
export const CAR_GROUPS = (COLLISION_GROUP_CAR << 16) | COLLISION_GROUP_WORLD;
export const WORLD_GROUPS = (COLLISION_GROUP_WORLD << 16) | 0xffff;
/** Groups used for the wheel shape casts: see the world, never the car. */
export const WHEEL_CAST_GROUPS = (COLLISION_GROUP_CAR << 16) | COLLISION_GROUP_WORLD;

/**
 * The slice of Rapier's raw (wasm-bindgen) query API the wheel casts use. World.castShape always
 * wraps the filter predicate in a JS closure, so the wasm calls back into JS for every candidate
 * collider, and it builds and frees a raw shape and three raw vectors per cast. Calling the raw
 * broad-phase query directly with no predicate and persistent raw inputs gives the same query with
 * none of that (≈2 MB/s less garbage at 240 Hz). Rapier is pinned at 0.21.0 (§2), which this matches.
 */
interface RawVec {
  x: number;
  y: number;
  z: number;
}
interface RawShapeCastHit {
  colliderHandle(): number;
  getComponents(out: Float32Array): void;
  free(): void;
}
interface RawBroadPhase {
  castShape(narrow: unknown, bodies: unknown, colliders: unknown, pos: RawVec, rot: unknown, vel: RawVec, shape: unknown, targetDistance: number, maxToi: number, stopAtPenetration: boolean, flags: number | undefined, groups: number | undefined, excludeCollider: number | undefined, excludeBody: number | undefined, predicate: undefined): RawShapeCastHit | undefined;
}
type RawWorld = { broadPhase: { raw: RawBroadPhase }; narrowPhase: { raw: unknown }; bodies: { raw: unknown }; colliders: { raw: unknown } };
/** Shape-cast hit components: time of impact, witness 1 (3), witness 2 (3), normal 1 (3), normal 2 (3). */
const castHit = new Float32Array(13);

const AIR_DENSITY = 1.2;
/** Low-speed contact-patch damping (N·s/m) that settles the tyre "springs" at rest. */
const LOW_SPEED_DAMPING = 9000;

export class WheelState {
  readonly index: number;
  readonly spec: WheelSpec;
  readonly susp: SuspensionSpec;
  readonly front: boolean;
  readonly left: boolean;
  /** Mount (top of travel) in car-local coordinates. */
  readonly mountLocal: V3;
  omega = 0;
  angle = 0;
  steer = 0;
  compression = 0;
  compressionVel = 0;
  contact = false;
  readonly contactPoint = v3();
  readonly contactNormal = v3(0, 1, 0);
  readonly centerWorld = v3();
  surface: SurfaceId = 'gravel';
  load = 0;
  suspForce = 0;
  /** Relaxed slip states. */
  kappa = 0;
  tanAlpha = 0;
  /** Instantaneous telemetry values. */
  slipRatio = 0;
  slipAngle = 0;
  vx = 0;
  vy = 0;
  fx = 0;
  fy = 0;
  brakeTorque = 0;
  combinedSlip = 0;
  /** Distance the cast travelled (for rendering the wheel position). */
  castDistance = 0;
  /**
   * Audio telemetry (no effect on the physics). Largest compression speed (m/s) while in contact since
   * a reader last cleared it: a pothole or bump lasts a few 240 Hz steps, between two rendered frames.
   */
  peakCompVel = 0;
  /** Seconds without ground contact so far (0 while in contact). */
  airTime = 0;
  /** The air time that ended at the last touchdown, until a reader clears it (0: none). */
  landedAfter = 0;

  constructor(index: number, spec: WheelSpec, susp: SuspensionSpec) {
    this.index = index;
    this.spec = spec;
    this.susp = susp;
    this.front = spec.axle === 'front';
    this.left = spec.side === 'left';
    this.mountLocal = v3(spec.position[0], spec.position[1] + susp.travel, spec.position[2]);
  }
}

export interface VehicleTelemetry {
  speed: number;
  forwardSpeed: number;
  rpm: number;
  gear: number;
  throttle: number;
  brake: number;
  steer: number;
  boost: number;
  gLong: number;
  gLat: number;
  gVert: number;
  bodySlipAngle: number;
  yawRate: number;
}

const tmpA = v3();
const tmpB = v3();
const tmpC = v3();
const tmpF = v3();
const tmpForce = v3();
/** Total force and torque (about the centre of mass) applied to the chassis this step. */
const accForce = v3();
const accTorque = v3();
const tmpR = v3();
const tyreOut: TyreForce = { fx: 0, fy: 0, dFxdK: 0, slip: 0 };

export class Vehicle {
  readonly spec: CarSpec;
  readonly body: RAPIER_NS.RigidBody;
  readonly collider: RAPIER_NS.Collider;
  readonly wheels: WheelState[];
  readonly drivetrain: Drivetrain;
  readonly telemetry: VehicleTelemetry = {
    speed: 0,
    forwardSpeed: 0,
    rpm: 0,
    gear: 1,
    throttle: 0,
    brake: 0,
    steer: 0,
    boost: 0,
    gLong: 0,
    gLat: 0,
    gVert: 0,
    bodySlipAngle: 0,
    yawRate: 0,
  };
  assists: Assists;
  /** Effective input after assists (for rendering steering wheel, pedals, lights). */
  readonly applied: ControlInput = { steer: 0, throttle: 0, brake: 0, handbrake: 0, shiftUp: false, shiftDown: false };
  /** Road-wheel steering angle (rad, + = left) before Ackermann split. */
  steerAngle = 0;

  private readonly R: RapierModule;
  private readonly world: RAPIER_NS.World;
  private readonly surfaces: SurfaceQuery;
  private readonly wheelShape: RAPIER_NS.Cylinder;
  /** Persistent raw inputs for the wheel casts (see RawBroadPhase). */
  private readonly rawWheelShape: unknown;
  private readonly rawCastPos: RawVec;
  private readonly rawCastVel: RawVec;
  private readonly castRot = q4();
  private readonly axisRot = q4();
  private readonly steerRot = q4();
  private readonly coeffCache = new Map<SurfaceId, TyreSurfaceCoeffs>();
  private readonly omegaArr = new Float64Array(4);
  private readonly inertiaArr = new Float64Array(4);
  private readonly torqueArr = new Float64Array(4);
  private readonly brakeArr = new Float64Array(4);
  private readonly gArr = new Float64Array(4);
  private readonly ePrev = new Float64Array(4);
  private readonly dampB = new Float64Array(4);
  private readonly prevVel = v3();
  private hasPrevVel = false;
  // Pose cache (updated in readPose)
  readonly pos = v3();
  readonly rot = q4();
  readonly linvel = v3();
  readonly angvel = v3();
  readonly com = v3();
  readonly up = v3(0, 1, 0);
  readonly fwd = v3(0, 0, 1);
  readonly leftDir = v3(1, 0, 0);
  private readonly identityRot = { x: 0, y: 0, z: 0, w: 1 };
  private shiftUpLatch = false;
  private shiftDownLatch = false;
  private brakeStillTimer = 0;
  /** Largest contact force magnitude reported for the chassis this step (N). */
  impactForce = 0;

  constructor(R: RapierModule, world: RAPIER_NS.World, spec: CarSpec, surfaces: SurfaceQuery, assists: Assists, seed = 1) {
    this.R = R;
    this.world = world;
    this.spec = spec;
    this.surfaces = surfaces;
    this.assists = assists;
    const c = spec.chassis;
    const bd = R.RigidBodyDesc.dynamic()
      .setTranslation(0, 1, 0)
      .setAdditionalMassProperties(
        c.mass,
        { x: c.centerOfMass[0], y: c.centerOfMass[1], z: c.centerOfMass[2] },
        { x: c.inertia[0], y: c.inertia[1], z: c.inertia[2] },
        { x: 0, y: 0, z: 0, w: 1 },
      )
      .setLinearDamping(0)
      .setAngularDamping(0.05)
      .setCcdEnabled(true)
      .setCanSleep(false);
    this.body = world.createRigidBody(bd);
    const pts = new Float32Array(c.hull.flat());
    const cd = (R.ColliderDesc.convexHull(pts) ?? R.ColliderDesc.cuboid(0.9, 0.5, 2))
      .setDensity(0)
      .setFriction(0.45)
      .setRestitution(0)
      .setRestitutionCombineRule(R.CoefficientCombineRule.Min)
      .setFrictionCombineRule(R.CoefficientCombineRule.Average)
      .setCollisionGroups(CAR_GROUPS)
      .setActiveEvents(R.ActiveEvents.CONTACT_FORCE_EVENTS)
      .setContactForceEventThreshold(2000);
    this.collider = world.createCollider(cd, this.body);
    this.wheels = spec.wheels.map((w, i) => new WheelState(i, w, w.axle === 'front' ? spec.suspension.front : spec.suspension.rear));
    // Cylinder shape cast: axis along the axle (car-local X), so tyre width and edges matter.
    this.wheelShape = new R.Cylinder(spec.tyre.width * 0.5, spec.tyre.radius);
    this.rawWheelShape = (this.wheelShape as unknown as { intoRaw(): unknown }).intoRaw();
    this.rawCastPos = R.VectorOps.intoRaw({ x: 0, y: 0, z: 0 }) as unknown as RawVec;
    this.rawCastVel = R.VectorOps.intoRaw({ x: 0, y: -1, z: 0 }) as unknown as RawVec;
    quatFromAxisAngle(this.axisRot, 0, 0, 1, Math.PI / 2);
    this.drivetrain = new Drivetrain(spec, seed);
  }

  private coeffs(id: SurfaceId): TyreSurfaceCoeffs {
    let c = this.coeffCache.get(id);
    if (!c) {
      c = tyreOnSurface(this.spec.tyre, SURFACES[id]);
      this.coeffCache.set(id, c);
    }
    return c;
  }

  /** Place the car at rest at a world position facing heading (rad about +Y; 0 = +Z). */
  placeAt(x: number, y: number, z: number, heading: number): void {
    const h = heading * 0.5;
    this.body.setTranslation({ x, y, z }, true);
    this.body.setRotation({ x: 0, y: Math.sin(h), z: 0, w: Math.cos(h) }, true);
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true);
    this.body.setAngvel({ x: 0, y: 0, z: 0 }, true);
    this.body.resetForces(true);
    this.body.resetTorques(true);
    for (const w of this.wheels) {
      w.omega = 0;
      w.kappa = 0;
      w.tanAlpha = 0;
      w.compression = 0;
      w.compressionVel = 0;
      w.peakCompVel = 0;
      w.airTime = 0;
      w.landedAfter = 0;
    }
    this.drivetrain.reset();
    this.hasPrevVel = false;
  }

  readPose(): void {
    const t = this.body.translation();
    const r = this.body.rotation();
    const lv = this.body.linvel();
    const av = this.body.angvel();
    const cm = this.body.worldCom();
    set(this.pos, t.x, t.y, t.z);
    this.rot.x = r.x;
    this.rot.y = r.y;
    this.rot.z = r.z;
    this.rot.w = r.w;
    set(this.linvel, lv.x, lv.y, lv.z);
    set(this.angvel, av.x, av.y, av.z);
    set(this.com, cm.x, cm.y, cm.z);
    rotate(this.up, this.rot, set(tmpA, 0, 1, 0));
    rotate(this.fwd, this.rot, set(tmpA, 0, 0, 1));
    rotate(this.leftDir, this.rot, set(tmpA, 1, 0, 0));
  }

  /** Accumulate force f applied at world point p (force plus torque about the centre of mass). */
  private forceAt(f: V3, p: V3): void {
    add(accForce, accForce, f);
    cross(tmpR, sub(tmpR, p, this.com), f);
    add(accTorque, accTorque, tmpR);
  }

  /** Velocity of the chassis at world point p. */
  private pointVelocity(out: V3, p: V3): V3 {
    sub(tmpC, p, this.com);
    cross(out, this.angvel, tmpC);
    return add(out, out, this.linvel);
  }

  /** One fixed simulation step (before world.step). */
  step(dt: number, rawInput: ControlInput): void {
    this.readPose();
    const spec = this.spec;
    const dtr = this.drivetrain;
    const fwdSpeed = dot(this.linvel, this.fwd);
    const speed = len(this.linvel);

    // --- Reverse logic: brake held at a standstill selects reverse; in reverse pedals swap.
    const input = this.applied;
    applyAssists(input, rawInput, this, dt);
    let throttle = input.throttle;
    let brake = input.brake;
    if (!dtr.inReverse) {
      if (Math.abs(fwdSpeed) < 0.6 && rawInput.brake > 0.5 && rawInput.throttle < 0.05) {
        this.brakeStillTimer += dt;
        if (this.brakeStillTimer > 0.35) {
          dtr.inReverse = true;
          dtr.requestShift(-1);
          this.brakeStillTimer = 0;
        }
      } else this.brakeStillTimer = 0;
    } else {
      // In reverse: brake pedal drives backwards, throttle brakes.
      throttle = input.brake;
      brake = input.throttle;
      if (rawInput.throttle > 0.3 && fwdSpeed > -0.8) {
        dtr.inReverse = false;
        dtr.requestShift(1);
        throttle = input.throttle;
        brake = 0;
      }
    }

    // --- Hill hold (automatic gearbox, as in a modern automatic): standing still with no pedals,
    // the brakes hold the car so it doesn't roll back down a climb; any pedal releases it.
    if (this.assists.autoGearbox && !dtr.inReverse && speed < 0.4 && rawInput.throttle < 0.02 && rawInput.brake < 0.02 && rawInput.handbrake < 0.05) brake = Math.max(brake, 0.35);

    // --- Gear selection.
    if (!dtr.inReverse) {
      if (this.assists.autoGearbox) this.autoShift(throttle, brake);
      else {
        if (input.shiftUp && !this.shiftUpLatch && dtr.gear < dtr.topGear) dtr.requestShift(dtr.gear <= 0 ? 1 : dtr.gear + 1);
        if (input.shiftDown && !this.shiftDownLatch && dtr.gear > 1) dtr.requestShift(dtr.gear - 1);
      }
      if (dtr.gear < 0 && !dtr.shifting) dtr.requestShift(1);
    }
    this.shiftUpLatch = input.shiftUp;
    this.shiftDownLatch = input.shiftDown;

    // --- Steering geometry with Ackermann.
    const maxSteer = spec.steering.maxAngleDeg * DEG;
    const delta = -input.steer * maxSteer; // + = left
    this.steerAngle = delta;
    const wb = Math.abs(spec.wheels[0].position[2] - spec.wheels[2].position[2]);
    const track = Math.abs(spec.wheels[0].position[0] - spec.wheels[1].position[0]);
    for (const w of this.wheels) {
      if (!w.front) {
        w.steer = 0;
        continue;
      }
      if (Math.abs(delta) < 1e-5) {
        w.steer = 0;
        continue;
      }
      // Turning left (delta>0): the left wheel is the inner wheel.
      const R = wb / Math.tan(Math.abs(delta));
      const inner = Math.atan(wb / (R - track / 2));
      const outer = Math.atan(wb / (R + track / 2));
      const isInner = delta > 0 ? w.left : !w.left;
      const ack = spec.steering.ackermann;
      const a = Math.abs(delta) + ack * ((isInner ? inner : outer) - Math.abs(delta));
      w.steer = Math.sign(delta) * a;
    }

    // --- Suspension: shape casts.
    for (const w of this.wheels) this.castWheel(w, dt);
    // Anti-roll bars.
    this.applyAntiRoll(0, 1, spec.suspension.front.antiRollRate);
    this.applyAntiRoll(2, 3, spec.suspension.rear.antiRollRate);

    // --- Tyres (pre-solve) and driveline.
    const bt = spec.brakes;
    const hb = input.handbrake;
    for (const w of this.wheels) {
      const i = w.index;
      const r = spec.tyre.radius;
      this.omegaArr[i] = w.omega;
      this.torqueArr[i] = 0;
      this.inertiaArr[i] = spec.tyre.inertia;
      this.gArr[i] = 0;
      this.dampB[i] = 0;
      let brakeT = brake * bt.maxTorque * (w.front ? bt.frontBias : 1 - bt.frontBias) * 0.5;
      if (!w.front) brakeT += hb * bt.handbrakeTorque * 0.5;
      if (this.assists.abs && brake > 0.05 && Math.abs(w.vx) > 3 && w.contact) {
        const c = this.coeffs(w.surface);
        const over = -w.kappa / c.slipPeak - 1.15;
        if (over > 0) brakeT *= clamp(1 - over * 2.5, 0.25, 1);
      }
      w.brakeTorque = brakeT;
      let rr = 0;
      if (w.contact && w.load > 0) {
        const surf = SURFACES[w.surface];
        rr = surf.rollingResistance * w.load * r;
        this.prepareTyre(w, dt);
        const c = this.coeffs(w.surface);
        const sigmaX = spec.tyre.relaxationLong;
        const avx = Math.abs(w.vx);
        const e = Math.exp((-avx * dt) / sigmaX);
        const g = avx > 1e-4 ? (1 - e) / avx : dt / sigmaX;
        this.ePrev[i] = e;
        this.gArr[i] = g;
        const kPred = w.kappa * e + g * (w.omega * r - w.vx);
        tyreForces(tyreOut, kPred, w.tanAlpha, w.load, c, spec.tyre);
        const b = 1 - smoothstep(0.5, 4, avx);
        this.dampB[i] = b;
        const fd = LOW_SPEED_DAMPING * b * (w.omega * r - w.vx);
        this.torqueArr[i] = -r * (tyreOut.fx + fd);
        this.inertiaArr[i] = spec.tyre.inertia + dt * r * r * (tyreOut.dFxdK * g + LOW_SPEED_DAMPING * b);
      }
      this.brakeArr[i] = brakeT + rr;
    }

    // Start-line launch: held at rest on the brakes with throttle → clutch out, launch limiter.
    dtr.launchHold = !dtr.inReverse && Math.abs(fwdSpeed) < 1 && throttle > 0.2 && (brake > 0.5 || hb > 0.5);
    dtr.step(dt, throttle, hb, this.omegaArr, this.inertiaArr, this.torqueArr, this.brakeArr);

    // --- Final tyre forces with the new wheel speeds, applied to the chassis.
    set(accForce, 0, 0, 0);
    set(accTorque, 0, 0, 0);
    const rc = spec.chassis.rollCenterHeight;
    for (const w of this.wheels) {
      const i = w.index;
      w.omega = this.omegaArr[i];
      w.angle += w.omega * dt;
      if (w.angle > 1e4 || w.angle < -1e4) w.angle %= Math.PI * 2;
      if (!w.contact || w.load <= 0) {
        w.fx = 0;
        w.fy = 0;
        w.kappa *= 0.9;
        w.tanAlpha *= 0.9;
        w.slipRatio = 0;
        w.slipAngle = 0;
        w.combinedSlip = 0;
        continue;
      }
      const r = spec.tyre.radius;
      const c = this.coeffs(w.surface);
      w.kappa = clamp(w.kappa * this.ePrev[i] + this.gArr[i] * (w.omega * r - w.vx), -30, 30);
      tyreForces(tyreOut, w.kappa, w.tanAlpha, w.load, c, spec.tyre);
      const b = this.dampB[i];
      let fx = tyreOut.fx + LOW_SPEED_DAMPING * b * (w.omega * r - w.vx);
      let fy = tyreOut.fy - LOW_SPEED_DAMPING * b * w.vy;
      // Friction limit for the damped total.
      const fmax = c.mu * w.load * 1.05;
      const fm = Math.hypot(fx, fy);
      if (fm > fmax && b > 0) {
        fx *= fmax / fm;
        fy *= fmax / fm;
      }
      w.fx = fx;
      w.fy = fy;
      w.combinedSlip = tyreOut.slip;
      const avx = Math.max(Math.abs(w.vx), 0.5);
      w.slipRatio = (w.omega * r - w.vx) / avx;
      w.slipAngle = Math.atan2(w.vy, Math.abs(w.vx) + 0.01);
      // Wheel heading directions on the contact plane.
      this.wheelAxes(w, tmpA, tmpB);
      scale(tmpForce, tmpA, fx);
      addScaled(tmpForce, tmpForce, tmpB, fy);
      // Wading drag (water, deep mud).
      const wd = SURFACES[w.surface].wadingDrag;
      if (wd > 0) {
        const v = Math.hypot(w.vx, w.vy);
        const k = wd * (w.load / 1000) * v;
        addScaled(tmpForce, tmpForce, tmpA, -k * w.vx);
        addScaled(tmpForce, tmpForce, tmpB, -k * w.vy);
      }
      // Apply at the contact point lifted to the roll-centre height.
      addScaled(tmpF, w.contactPoint, this.up, rc);
      this.forceAt(tmpForce, tmpF);
    }
    // Suspension forces along the chassis up axis at each wheel.
    for (const w of this.wheels) {
      if (w.suspForce <= 0) continue;
      scale(tmpForce, this.up, w.suspForce);
      this.forceAt(tmpForce, w.centerWorld);
    }
    // Aerodynamics: drag at the centre of mass, lift/downforce at the axles.
    const c = spec.chassis;
    scale(tmpForce, this.linvel, -0.5 * AIR_DENSITY * c.dragArea * speed);
    add(accForce, accForce, tmpForce);
    const q = 0.5 * AIR_DENSITY * fwdSpeed * fwdSpeed;
    if (q > 1) {
      const fz = this.wheels[0].spec.position[2];
      const rz = this.wheels[2].spec.position[2];
      rotate(tmpA, this.rot, set(tmpB, 0, 0, fz));
      add(tmpA, tmpA, this.pos);
      scale(tmpForce, this.up, q * c.liftAreaFront);
      this.forceAt(tmpForce, tmpA);
      rotate(tmpA, this.rot, set(tmpB, 0, 0, rz));
      add(tmpA, tmpA, this.pos);
      scale(tmpForce, this.up, q * c.liftAreaRear);
      this.forceAt(tmpForce, tmpA);
    }

    // Hand the totals to Rapier: same result as per-point forces, a fraction of the calls.
    this.body.resetForces(false);
    this.body.resetTorques(false);
    this.body.addForce(accForce, false);
    this.body.addTorque(accTorque, false);

    // --- Telemetry.
    const t = this.telemetry;
    t.speed = speed;
    t.forwardSpeed = fwdSpeed;
    t.rpm = dtr.rpm;
    t.gear = dtr.shifting ? dtr.targetGear : dtr.gear;
    t.throttle = dtr.throttleEff;
    t.brake = brake;
    t.steer = input.steer;
    t.boost = dtr.boost;
    if (this.hasPrevVel) {
      const ax = (this.linvel.x - this.prevVel.x) / dt;
      const ay = (this.linvel.y - this.prevVel.y) / dt + 9.81;
      const az = (this.linvel.z - this.prevVel.z) / dt;
      set(tmpA, ax, ay, az);
      const k = 1 - Math.exp(-dt / 0.08);
      t.gLong += (dot(tmpA, this.fwd) / 9.81 - t.gLong) * k;
      t.gLat += (dot(tmpA, this.leftDir) / 9.81 - t.gLat) * k;
      t.gVert += (dot(tmpA, this.up) / 9.81 - t.gVert) * k;
    }
    copy(this.prevVel, this.linvel);
    this.hasPrevVel = true;
    const latSpeed = dot(this.linvel, this.leftDir);
    t.bodySlipAngle = speed > 2 ? Math.atan2(latSpeed, Math.abs(fwdSpeed)) : 0;
    t.yawRate = dot(this.angvel, this.up);
  }

  /** Forward/left unit axes of a wheel on its contact plane. */
  private wheelAxes(w: WheelState, fOut: V3, lOut: V3): void {
    const n = w.contactNormal;
    // Rotate chassis forward about chassis up by the steer angle.
    const cs = Math.cos(w.steer);
    const sn = Math.sin(w.steer);
    // forward' = fwd*cos + left*sin  (positive steer turns toward +left)
    set(fOut, this.fwd.x * cs + this.leftDir.x * sn, this.fwd.y * cs + this.leftDir.y * sn, this.fwd.z * cs + this.leftDir.z * sn);
    const d = dot(fOut, n);
    addScaled(fOut, fOut, n, -d);
    normalize(fOut, fOut);
    cross(lOut, n, fOut);
    normalize(lOut, lOut);
  }

  /** Compute contact-patch velocities and relax the lateral slip state. */
  private prepareTyre(w: WheelState, dt: number): void {
    this.wheelAxes(w, tmpA, tmpB);
    this.pointVelocity(tmpC, w.contactPoint);
    w.vx = dot(tmpC, tmpA);
    w.vy = dot(tmpC, tmpB);
    const sigma = this.spec.tyre.relaxationLat;
    const avx = Math.abs(w.vx);
    const e = Math.exp((-avx * dt) / sigma);
    const g = avx > 1e-4 ? (1 - e) / avx : dt / sigma;
    w.tanAlpha = clamp(w.tanAlpha * e + g * w.vy, -30, 30);
  }

  private castWheel(w: WheelState, dt: number): void {
    const s = w.susp;
    const travel = s.travel;
    rotate(tmpA, this.rot, w.mountLocal);
    add(tmpA, tmpA, this.pos);
    const rp = this.rawCastPos;
    rp.x = tmpA.x;
    rp.y = tmpA.y;
    rp.z = tmpA.z;
    const rv = this.rawCastVel;
    rv.x = -this.up.x;
    rv.y = -this.up.y;
    rv.z = -this.up.z;
    quatFromAxisAngle(this.steerRot, 0, 1, 0, w.steer);
    quatMul(this.castRot, this.rot, this.steerRot);
    quatMul(this.castRot, this.castRot, this.axisRot);
    const world = this.world as unknown as RawWorld;
    const rawRot = this.R.RotationOps.intoRaw(this.castRot) as unknown as { free(): void };
    const raw = world.broadPhase.raw.castShape(world.narrowPhase.raw, world.bodies.raw, world.colliders.raw, rp, rawRot, rv, this.rawWheelShape, 0, travel, true, undefined, WHEEL_CAST_GROUPS, undefined, this.body.handle, undefined);
    rawRot.free();
    let hitT = -1;
    if (raw) {
      raw.getComponents(castHit);
      raw.free();
      hitT = castHit[0];
    }
    const prev = w.compression;
    const wasInContact = w.contact;
    if (hitT >= 0) {
      const dist = clamp(hitT, 0, travel);
      w.castDistance = dist;
      w.compression = travel - dist;
      w.contact = true;
      set(w.contactPoint, castHit[1], castHit[2], castHit[3]);
      set(w.contactNormal, castHit[7], castHit[8], castHit[9]);
      normalize(w.contactNormal, w.contactNormal);
      if (dot(w.contactNormal, this.up) < 0.05) {
        // Grazing a wall: no vertical support from this contact.
        w.contact = false;
      }
      w.surface = this.surfaces.surfaceAt(w.contactPoint.x, w.contactPoint.z);
    } else {
      w.castDistance = travel;
      w.compression = 0;
      w.contact = false;
    }
    addScaled(w.centerWorld, tmpA, this.up, -w.castDistance);
    w.compressionVel = (w.compression - prev) / dt;
    if (w.contact) {
      // A touchdown jumps from full droop in one step: that speed is not a bump.
      if (wasInContact) {
        if (w.compressionVel > w.peakCompVel) w.peakCompVel = w.compressionVel;
      } else if (w.airTime > 0) w.landedAfter = w.airTime;
      w.airTime = 0;
    } else w.airTime += dt;
    if (!w.contact) {
      w.suspForce = 0;
      w.load = 0;
      return;
    }
    let f = s.preload + s.springRate * w.compression;
    const v = w.compressionVel;
    const knee = s.dampingKnee;
    const cd = v > 0 ? s.bumpDamping : s.reboundDamping;
    const av = Math.abs(v);
    const damp = av <= knee ? cd * av : cd * knee + cd * s.highSpeedFactor * (av - knee);
    f += v > 0 ? damp : -damp;
    const bsStart = travel - s.bumpStopLength;
    if (w.compression > bsStart) {
      const x = w.compression - bsStart;
      f += s.bumpStopRate * x * (1 + x / s.bumpStopLength * 3);
    }
    w.suspForce = Math.max(0, f);
    w.load = w.suspForce * Math.max(0, dot(this.up, w.contactNormal));
  }

  private applyAntiRoll(a: number, b: number, rate: number): void {
    const wa = this.wheels[a];
    const wb = this.wheels[b];
    const d = (wa.compression - wb.compression) * rate;
    if (wa.contact) {
      wa.suspForce = Math.max(0, wa.suspForce + d);
      wa.load = wa.suspForce * Math.max(0, dot(this.up, wa.contactNormal));
    }
    if (wb.contact) {
      wb.suspForce = Math.max(0, wb.suspForce - d);
      wb.load = wb.suspForce * Math.max(0, dot(this.up, wb.contactNormal));
    }
  }

  private autoShift(throttle: number, brake: number): void {
    const d = this.drivetrain;
    if (d.shifting || d.shiftCooldown > 0) return;
    const gb = this.spec.gearbox;
    // Ground-speed based engine speed (ignores wheelspin) for the decision.
    const r = this.spec.tyre.radius;
    let vWheel = 0;
    let n = 0;
    for (const w of this.wheels) {
      if (w.contact) {
        vWheel += w.vx;
        n++;
      }
    }
    const axleOmega = n > 0 ? vWheel / n / r : (this.omegaArr[0] + this.omegaArr[1]) * 0.5;
    const rpmNow = d.rpm;
    if (d.gear <= 0) {
      d.requestShift(1);
      return;
    }
    if (d.gear < d.topGear && rpmNow > gb.autoUpRpm && throttle > 0.3) {
      d.requestShift(d.gear + 1);
      return;
    }
    if (d.gear > 1) {
      const lowerRpm = d.rpmAtAxle(d.gear - 1, axleOmega);
      const downAt = brake > 0.2 ? gb.autoDownRpm + 1100 : gb.autoDownRpm;
      if (d.rpmAtAxle(d.gear, axleOmega) < downAt && lowerRpm < gb.autoUpRpm - 600) d.requestShift(d.gear - 1);
    }
  }

  /** Collision impulse feedback from the physics world. */
  noteImpact(force: number): void {
    if (force > this.impactForce) this.impactForce = force;
  }
}

/** Rotate a car-local point to world space given position and rotation. */
export function localToWorld(out: V3, pos: V3, rot: Q4, local: V3): V3 {
  rotate(out, rot, local);
  return add(out, out, pos);
}
